import {
  ChangeEvent,
  ContentType,
  FileEntryBM,
  FoodMenuData,
  FoodMenuItem,
  jsonCopy,
} from '@bm-js/atlascloud-shared'
import MarginOptions from './MarginOptions'
import RightBarGroup from './RightBarGroup'
import { TrKey } from '../cnst/translations/trKey.cnst'
import Input from './Input'
import { useTr } from '../utils/useTr'
import React, { useMemo } from 'react'
import { Button } from '@bm-js/atlascloud-ui'
import { v4 as uuid } from 'uuid'

type Props = {
  content: ContentType<FoodMenuData>
  setData: (e: ChangeEvent<FoodMenuItem[] | string>) => void
}

const FoodMenuEditor = ({ content, setData }: Props) => {
  const { tr } = useTr()

  const onChange = (
    e: ChangeEvent<FileEntryBM | number | string>,
    i: number,
    key: keyof FoodMenuItem
  ) => {
    const { value } = e.target
    const updated = jsonCopy(content.data)

    if (key === 'image') {
      updated.items[i].image = value as FileEntryBM
    } else if (key === 'price') {
      updated.items[i].price = value as number
    } else {
      updated.items[i][key] = value as string
    }

    const updateEvent = {
      target: {
        name: 'items',
        value: updated.items,
      },
    }
    setData(updateEvent)
  }

  const removeItem = (i: number) => {
    const updated = [...content.data.items]
    updated.splice(i, 1)
    const updateEvent = {
      target: { name: 'items', value: updated },
    }
    setData(updateEvent)
  }

  const addItem = () => {
    const updated = [...content.data.items]
    updated.push({ title: '', price: 0, description: '', uuid: uuid() })
    const updateEvent = { target: { name: 'items', value: updated } }
    setData(updateEvent)
  }

  const existingCategories = useMemo(() => {
    return content.data.items
      .map((item) => item.category)
      .filter((category) => category !== undefined)
  }, [content.data.items])

  return (
    <>
      <MarginOptions setData={setData} content={content} />
      <RightBarGroup title={tr(TrKey.foodMenuEditorItemsTitle)}>
        {content.data.items.map((item, i) => (
          <React.Fragment key={`foodMenu_item_${content.uuid}_${i}`}>
            <Input
              light
              label={tr(TrKey.foodMenuEditorItemsTitleLabel)}
              desc={tr(TrKey.foodMenuEditorItemsTitleDescription)}
              value={item.title}
              onChange={(e) => onChange(e, i, 'title')}
            />
            <Input
              light
              label={tr(TrKey.foodMenuEditorItemsPriceLabel)}
              desc={tr(TrKey.foodMenuEditorItemsPriceDescription)}
              value={item.price}
              onChange={(e) => onChange(e, i, 'price')}
            />
            <Input
              light
              label={tr(TrKey.foodMenuEditorItemsDescriptionLabel)}
              desc={tr(TrKey.foodMenuEditorItemsDescriptionDescription)}
              value={item.description}
              onChange={(e) => onChange(e, i, 'description')}
            />
            <datalist id='food-menu-categories'>
              {existingCategories.map((category, i) => (
                <option key={`category_${category}_${i}`} value={category} />
              ))}
            </datalist>
            <Input
              list='food-menu-categories'
              light
              label={tr(TrKey.foodMenuEditorItemsCategoryLabel)}
              desc={tr(TrKey.foodMenuEditorItemsCategoryDescription)}
              value={item.category}
              onChange={(e) => onChange(e, i, 'category')}
            />
            <Button danger sm onClick={() => removeItem(i)}>
              {tr(TrKey.remove)}
            </Button>
            <div className='bg-slate-800 h-px my-4'></div>
          </React.Fragment>
        ))}

        <div className='flex justify-end'>
          <Button onClick={() => addItem()}>{tr(TrKey.editorAdd)}</Button>
        </div>
      </RightBarGroup>
    </>
  )
}

export default FoodMenuEditor
